"use client";

import { useSyncExternalStore } from "react";

type SignOut = (redirectUrl: string) => Promise<unknown>;

let ending = false;
let hasClerkSession: () => boolean = () => false;
let clerkSignOut: SignOut | null = null;
const listeners = new Set<() => void>();

export function bindClerk(hasSession: () => boolean, signOut: SignOut) {
  hasClerkSession = hasSession;
  clerkSignOut = signOut;
}

export function isEndingSession() {
  return ending;
}

export function markEnding() {
  ending = true;
  listeners.forEach((l) => l());
}

export const ENDED_SESSION_KEY = "wexba_ended_session";

function loginUrl(reason: string) {
  return reason && reason !== "signed_out" ? `/login?reason=${encodeURIComponent(reason)}` : "/login";
}

export function goToLogin(reason: string) {
  window.location.replace(loginUrl(reason));
}

export async function fetchEndReason(sessionId: string) {
  try {
    const res = await fetch("/api/session-end", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId }),
    });
    if (!res.ok) return "ended";
    const data = await res.json();
    return typeof data?.reason === "string" ? data.reason : "ended";
  } catch {
    return "ended";
  }
}

/**
 * Cierra la sesión por un motivo conocido (aviso del servidor por socket) y va
 * al login con ese motivo. Si Clerk ya no tiene sesión, el cierre lo está
 * manejando SessionWatcher (p. ej. el usuario salió con el UserButton).
 */
export async function endSession(reason: string) {
  if (ending || !hasClerkSession()) return;
  markEnding();
  // signOut de Clerk navega solo a su URL de salida; se le pasa la del login
  // con el motivo para que esa navegación no lo pierda.
  try {
    await clerkSignOut?.(loginUrl(reason));
  } catch {
    // La sesión ya estaba revocada en Clerk; basta con salir.
  }
  goToLogin(reason);
}

export function useEndingSession() {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => ending,
    () => false
  );
}
